import * as React from "react"
import { useSearchParams } from "react-router-dom"
import { type DateRange } from "react-day-picker"
import { subDays } from "date-fns"
import { IconFileText } from "@tabler/icons-react"
import { FilterContainer } from "@/components/FiltersV2"
import { CustomDatePicker } from "@/components/CustomDatePicker"
import { ModelsSelector } from "@/components/ModelsSelector"
import { ModalitySelector, ALL_MODALITIES_SELECTION } from "@/components/ModalitySelector"
import { ToolsSelector, ALL_TOOLS_SELECTION } from "@/components/ToolsSelector"
import { FiltersSelector } from "@/components/FiltersSelector"
import { DataTable } from "@/components/ui/data-table"
import { TransactionDetailsSidebar } from "@/components/Activity"
import { useActivityLogs } from "@/data/hooks/api/useActivityLogs"
import { useSavedViews, relativeDateToRange } from "@/contexts/SavedViewsContext"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/useAuth"
import { UpgradeBanner } from "@/components/UpgradeBanner"
import type { ActivityLogEntry, ActivityFilters, ModelSelection, ModalitySelection, ToolsSelection } from "@/data/types/analytics.types"
import type { SortingState } from "@tanstack/react-table"
import type { LogEntry } from "@/data/types/costExplorer.types"
import { logsTableColumns } from "./logic"

// Map API response to LogEntry format
function mapApiResponseToLogEntry(entry: ActivityLogEntry): LogEntry {
  // Handle potential API response variations
  const timestamp = entry.timestamp || (entry as any).created_at || ""
  const cost = typeof entry.cost === "string" ? parseFloat(entry.cost) : entry.cost
  const metadata = entry.metadata || (entry as any).request_metadata || {}
  const tools = (entry as any).tools || {}
  const usage = (entry as any).usage || "0 tokens"

  return {
    id: entry.id,
    timestamp,
    provider: entry.provider || "",
    model: entry.model,
    cost: isNaN(cost) ? 0 : cost,
    usage,
    tools: tools as Record<string, string>,
    metadata: metadata as Record<string, string>,
    modality: (entry as any).modality || "",
  }
}

// Map column ID to API sort_by field
function mapColumnToSortBy(columnId: string): string | undefined {
  const mapping: Record<string, string> = {
    timestamp: "timestamp",
    provider: "provider",
    model: "model",
    cost: "cost",
  }
  return mapping[columnId]
}

// Default date range (last 30 days)
const DEFAULT_DATE_RANGE: DateRange = {
  from: subDays(new Date(), 29),
  to: new Date(),
}

// Default selections for models
const ALL_MODELS_SELECTION: ModelSelection = { all: true, providers: [], models: [] }

export function ActivityPage() {
  const { isOnFreePlan } = useAuth()
  const { currentFilters, setCurrentFilters, setAllFiltersState, appliedView, clearAppliedView } = useSavedViews()
  const [searchParams, setSearchParams] = useSearchParams()

  // Sidebar state
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false)

  // Check for transaction_id in URL on mount
  React.useEffect(() => {
    const transactionId = searchParams.get("transaction_id")
    if (transactionId) {
      setSelectedTransactionId(transactionId)
      setIsSidebarOpen(true)
    }
  }, []) // Only run on mount

  // Handle sidebar open/close
  const handleSidebarOpenChange = React.useCallback((open: boolean) => {
    setIsSidebarOpen(open)
    if (!open) {
      // Remove transaction_id from URL when closing
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete("transaction_id")
        return newParams
      })
      setSelectedTransactionId(null)
    }
  }, [setSearchParams])

  // Handle row click to open sidebar
  const handleRowClick = React.useCallback((row: LogEntry) => {
    setSelectedTransactionId(row.id)
    setIsSidebarOpen(true)
    // Update URL with transaction_id
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      newParams.set("transaction_id", row.id)
      return newParams
    })
  }, [setSearchParams])

  // Filter state - these are the "working" filters that change as user interacts
  const [workingDateRange, setWorkingDateRange] = React.useState<DateRange | undefined>(DEFAULT_DATE_RANGE)
  const [modelSelection, setModelSelection] = React.useState<ModelSelection>(ALL_MODELS_SELECTION)
  const [modalitySelection, setModalitySelection] = React.useState<ModalitySelection>(ALL_MODALITIES_SELECTION)
  const [toolsSelection, setToolsSelection] = React.useState<ToolsSelection>(ALL_TOOLS_SELECTION)

  // Applied filters - these are the filters actually used for API requests
  const [appliedDateRange, setAppliedDateRange] = React.useState<DateRange | undefined>(DEFAULT_DATE_RANGE)

  // Handle when a saved view is applied - update all filter states
  React.useEffect(() => {
    if (!appliedView) return

    // Update date range from relative date or use default
    const dateRange = relativeDateToRange(appliedView.relativeDate) || DEFAULT_DATE_RANGE
    setWorkingDateRange(dateRange)
    setAppliedDateRange(dateRange)

    // Update models if specified in the view
    if (appliedView.modelSelection) {
      setModelSelection(appliedView.modelSelection)
    } else if (appliedView.models && appliedView.models.length > 0) {
      // Legacy support for old views with models array
      setModelSelection({ all: false, providers: [], models: appliedView.models })
    }

    // Reset pagination
    setPageIndex(0)

    // Clear the applied view after processing to prevent re-triggering
    clearAppliedView()

    toast.success(`Applied view: ${appliedView.title}`)
  }, [appliedView, clearAppliedView])

  // Sync all filter state with SavedViewsContext for saving views
  React.useEffect(() => {
    setAllFiltersState({
      dateRange: appliedDateRange,
      granularity: 'daily', // Activity page doesn't have granularity selector
      dimension: 'model', // Activity page groups by model
      modelSelection,
      providers: undefined,
      metadataFilters: currentFilters,
      // Note: modalitySelection and toolsSelection could be added to SavedViewsContext
      // if we want to persist them in saved views
    })
  }, [appliedDateRange, modelSelection, currentFilters, setAllFiltersState])

  // Table state
  const [pageIndex, setPageIndex] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "timestamp", desc: true }, // Default sort by timestamp descending
  ])

  // Build filters object for API
  const buildFilters = React.useCallback((): ActivityFilters | null => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return null

    // Convert date range to ISO strings
    const startDate = new Date(appliedDateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(appliedDateRange.to)
    endDate.setHours(23, 59, 59, 999)

    // Convert metadata filters from SavedViewsContext format to API format
    const metadataFilters = currentFilters
      .filter((f) => f.tagName && f.operation && f.value)
      .map((f) => ({
        key: f.tagName,
        operation: (f.operation === 'is' ? 'is' : 'is_not') as 'is' | 'is_not',
        value: f.value,
      }))

    // Determine sort parameters
    const sortEntry = sorting[0]
    const sortBy = sortEntry ? mapColumnToSortBy(sortEntry.id) : "timestamp"
    const sortOrder = sortEntry?.desc ? "desc" : "asc"

    // Build filters object
    const filters: ActivityFilters = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      skip: pageIndex * pageSize,
      limit: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder as 'asc' | 'desc',
    }

    // Add optional filters
    if (modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0) {
      filters.model_selection = modelSelection
    }

    if (metadataFilters.length > 0) {
      filters.metadata_filters = metadataFilters
    }

    // Add modality filter (only if not "all")
    if (!modalitySelection.all && modalitySelection.modalities.length > 0) {
      filters.modalities = modalitySelection.modalities
    }

    // Add tools filter (only if not "all")
    if (!toolsSelection.all && toolsSelection.tools.length > 0) {
      filters.tools = toolsSelection.tools
    }

    return filters
  }, [appliedDateRange, modelSelection, modalitySelection, toolsSelection, currentFilters, pageIndex, pageSize, sorting])

  const filters = buildFilters()

  // Use React Query hook to fetch activity logs
  // React Query will automatically refetch when the queryKey (filters) changes
  const { data: response, isLoading } = useActivityLogs(filters, true)

  // Map API response to LogEntry format
  const data = React.useMemo(() => {
    if (!response?.items) return []
    return response.items.map(mapApiResponseToLogEntry)
  }, [response])

  const totalItems = response?.total ?? 0
  const loading = isLoading

  // Handle apply button click from date picker
  const handleDateRangeApply = React.useCallback((range: DateRange | undefined) => {
    setAppliedDateRange(range)
    setPageIndex(0) // Reset to first page when date range changes
  }, [])

  // Handle page change
  const handlePageChange = React.useCallback((newPageIndex: number) => {
    setPageIndex(newPageIndex)
  }, [])

  // Handle page size change
  const handlePageSizeChange = React.useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPageIndex(0) // Reset to first page when page size changes
  }, [])

  // Reset all filters to initial page load state
  const handleClearAllFilters = React.useCallback(() => {
    // Reset models to all models
    setModelSelection(ALL_MODELS_SELECTION)
    // Reset modality to all modalities
    setModalitySelection(ALL_MODALITIES_SELECTION)
    // Reset tools to all tools
    setToolsSelection(ALL_TOOLS_SELECTION)
    // Reset date range to default
    setWorkingDateRange(DEFAULT_DATE_RANGE)
    setAppliedDateRange(DEFAULT_DATE_RANGE)
    // Reset pagination
    setPageIndex(0)
    // Clear metadata filters
    setCurrentFilters([])
  }, [setCurrentFilters])

  // Check if any filter has changed from initial state
  const hasFiltersChanged = React.useMemo(() => {
    // Check if date range changed from default (last 30 days)
    const defaultFrom = subDays(new Date(), 29)
    const defaultTo = new Date()
    const dateChanged = appliedDateRange?.from && appliedDateRange?.to && (
      appliedDateRange.from.toDateString() !== defaultFrom.toDateString() ||
      appliedDateRange.to.toDateString() !== defaultTo.toDateString()
    )
    // Check if models changed from default (all models)
    const modelsChanged = !modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0
    // Check if modality changed from default (all modalities)
    const modalityChanged = !modalitySelection.all && modalitySelection.modalities.length > 0
    // Check if tools changed from default (all tools)
    const toolsChanged = !toolsSelection.all && toolsSelection.tools.length > 0
    // Check if any metadata filters exist (including incomplete ones being edited)
    const hasMetadataFilters = currentFilters.length > 0
    return dateChanged || modelsChanged || modalityChanged || toolsChanged || hasMetadataFilters
  }, [appliedDateRange, modelSelection, modalitySelection, toolsSelection, currentFilters])

  return (
    <div>
      {/* Filter Rows - Same as Breakdown page */}
      <FilterContainer>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CustomDatePicker
              value={workingDateRange}
              onChange={setWorkingDateRange}
              onApply={handleDateRangeApply}
            />
            <ModelsSelector value={modelSelection} onChange={setModelSelection} />
            <ModalitySelector value={modalitySelection} onChange={setModalitySelection} />
            <ToolsSelector value={toolsSelection} onChange={setToolsSelection} />
          </div>
          {hasFiltersChanged && (
            <div className="flex items-center gap-2 shrink-0">
              <div className="h-[30px] w-px bg-[lch(83.025_0_282.863)]" />
              <Button
                size="xs"
                variant="ghost"
                onClick={handleClearAllFilters}
              >
                Reset Filters
              </Button>
            </div>
          )}
        </div>
      </FilterContainer>
      <FilterContainer>
        <FiltersSelector onClearAll={handleClearAllFilters} hideResetButton />
      </FilterContainer>
      {isOnFreePlan && (
        <UpgradeBanner type="data-retention" />
      )}

      {/* Table Section - Same as API Keys page */}
      <div className="mx-4 lg:mx-6 my-6 space-y-[13px]">
        <div className="flex items-center justify-between">
          <div className="space-y-[2px]">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">Request Logs</h2>
            <p className="text-[13px] text-[#8A8F98] px-[4px]">Complete history of all API requests</p>
          </div>
        </div>
        <DataTable
          data={data}
          columns={logsTableColumns}
          loading={loading}
          getRowId={(row) => row.id}
          emptyMessage="No logs found"
          emptyIcon={<IconFileText className="size-6 text-[#8A8F98]" />}
          enableSorting={true}
          sorting={sorting}
          onSortingChange={setSorting}
          onRowClick={handleRowClick}
          pagination={{
            enabled: true,
            mode: "server",
            pageIndex,
            pageSize,
            totalItems,
            pageSizeOptions: [10, 25, 50, 100],
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
      </div>

      {/* Transaction Details Sidebar */}
      <TransactionDetailsSidebar
        open={isSidebarOpen}
        onOpenChange={handleSidebarOpenChange}
        transactionId={selectedTransactionId}
      />
    </div>
  )
}
